import type { ContractCategory } from '@/features/contracts';
import type {
  ConsumoOrigemRef,
  Fatura,
  FaturaStatus,
  MetodoPagamento,
} from '@/features/faturas/types';

export type AlertaFinanceiroNivel = 'INFO' | 'WARNING' | 'DANGER';

export type SituacaoAtualTipo =
  | 'FATURA_EM_ABERTO'
  | 'FATURA_PROXIMA_VENCIMENTO'
  | 'FATURA_VENCIDA'
  | 'FATURA_ERRO_EMISSAO'
  | 'AGUARDANDO_CONFIRMACAO'
  | 'TUDO_EM_DIA'
  | 'SEM_FATURAS';

export interface SituacaoAtual {
  tipo: SituacaoAtualTipo;
  faturaAtiva: Fatura | null;
  diasAteVencimento: number | null;
  diasEmAtraso: number | null;
}

export type PlanoPeriodicidade = 'MENSAL' | 'ANUAL' | 'AVULSO' | 'HIBRIDO';

export type TipoClienteEscritorio = 'CONVENIADO' | 'AVULSO';

export type PlanoContratadoStatus = 'ATIVO' | 'TRIAL' | 'SUSPENSO';

export interface PlanoContratado {
  id: string;
  produtoNome: string;
  descricaoCurta: string | null;
  categoria: ContractCategory | null;
  periodicidade: PlanoPeriodicidade;
  tipoCliente: TipoClienteEscritorio;
  diaVencimento: number | null;
  metodoPagamentoPadrao: MetodoPagamento;
  valorBase: number | null;
  status: PlanoContratadoStatus;
  contratadoEm: string;
  ehPlanoPrincipal: boolean;
}

export interface PrevisaoProximaFatura {
  disponivel: boolean;
  valorEstimado: number | null;
  periodoInicio: string | null;
  periodoFim: string | null;
  dataPrevistaEmissao: string | null;
  ehTrial: boolean;
  trialMensagem: string | null;
  mensagemAlternativa: string | null;
}

export interface AlertaFinanceiroGlobal {
  faturaId: string;
  numero: string;
  valor: number;
  dataVencimento: string;
  nivel: AlertaFinanceiroNivel;
  mensagem: string;
}

export interface PainelFinanceiroResumo {
  situacaoAtual: SituacaoAtual;
  previsaoProximaFatura: PrevisaoProximaFatura;
  produtosContratados: PlanoContratado[];
  alertaGlobal: AlertaFinanceiroGlobal | null;
}

export interface PainelFinanceiroFilters {
  busca: string;
  status: FaturaStatus | 'TODOS';
  ano: number | null;
}

export type ConsumoEmAbertoStatus = 'DISPONIVEL' | 'EM_GERACAO';

export type ModoAgrupamentoAntecipacao = 'UNICA' | 'POR_TIPO' | 'POR_ITEM';

export interface ConsumoEmAberto {
  id: string;
  eventIds: string[];
  data: string;
  categoria: string;
  servicoLabel: string;
  origemPrincipal: ConsumoOrigemRef;
  vinculacoes: ConsumoOrigemRef[];
  destinatarioNome: string | null;
  destinatarioDocumento: string | null;
  valor: number;
  observacao: string | null;
  status: ConsumoEmAbertoStatus;
}

export interface AntecipacaoPagamentoFormData {
  itensIds: string[];
  modoAgrupamento: ModoAgrupamentoAntecipacao;
}

export interface AntecipacaoPagamentoResultado {
  faturasGeradas: Fatura[];
  itensFaturados: string[];
}

export type EstornoStatus = 'SOLICITADO' | 'EM_PROCESSAMENTO' | 'CONCLUIDO' | 'FALHOU';

export type MetodoDevolucao = 'PIX' | 'TED' | 'ESTORNO_CARTAO' | 'CREDITO_CONTA';

export interface Estorno {
  id: string;
  faturaId: string;
  faturaNumero: string;
  faturaValorOriginal: number;
  valorEstornado: number;
  ehEstornoParcial: boolean;
  status: EstornoStatus;
  motivo: string;
  motivoDetalhado: string | null;
  metodoDevolucao: MetodoDevolucao;
  contaDestinoMascarada: string | null;
  solicitadoEm: string;
  concluidoEm: string | null;
  solicitadoPor: string;
  observacaoArbitralis: string | null;
}

export interface EstornosFilters {
  busca: string;
  status: EstornoStatus | 'TODOS';
  ano: number | null;
}

export const DEFAULT_ESTORNOS_FILTERS: EstornosFilters = {
  busca: '',
  status: 'TODOS',
  ano: null,
};

export interface FinanceiroEscritorioState {
  resumo: PainelFinanceiroResumo | null;
  faturas: Fatura[];
  faturaAtual: Fatura | null;
  filters: PainelFinanceiroFilters;
  consumoEmAberto: ConsumoEmAberto[];
  consumoEmAbertoLoading: boolean;
  estornos: Estorno[];
  estornosLoading: boolean;
  estornosFilters: EstornosFilters;
  loading: boolean;
  loadingDetalhe: boolean;
  saving: boolean;
  error: string | null;
  bannerDispensadoNaSessao: boolean;
}

export const DEFAULT_PAINEL_FILTERS: PainelFinanceiroFilters = {
  busca: '',
  status: 'TODOS',
  ano: null,
};
